import { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation } from "react-router-dom";
import Home from "./components/home";
import Services from "./components/services";
import Technologies from "./components/technologies";
import Portfolio from "./components/portfolio";
import About from "./components/about";
import Contact from "./components/contact";
import Careers from "./components/careers";
import BuildWebsite from "./components/buildwebsites";
import AdCreatives from "./components/adcreatives";
import Consultation from "./components/consultation";
import APIGateways from "./components/apigateways";
import WebApplications from "./components/webapplications";
import MobileApps from "./components/mobileapps";
import WebScrapers from "./components/webscrappers";
import CRMPlatforms from "./components/crmplatforms";
import DesktopApps from "./components/desktopapps";
import AIAgents from "./components/aiagents";
import SiteAtmosphere from "./components/utilities/siteatmosphere";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const routeMeta = {
  "/": {
    title: "Orvitrix | Product Engineering & Intelligent Automation",
    description: "Orvitrix builds web platforms, mobile apps, CRM systems, APIs, data workflows, and practical AI agents for growing teams.",
  },
  "/services": {
    title: "Digital Product & Automation Services | Orvitrix",
    description: "Compare Orvitrix services for web, mobile, CRM, APIs, automation, AI agents, data collection, and product strategy.",
  },
  "/technologies": {
    title: "Technology & Engineering Approach | Orvitrix",
    description: "See how Orvitrix uses modern frontend, backend, cloud, data, and AI technologies to build dependable digital products.",
  },
  "/portfolio": {
    title: "Product Engineering Work | Orvitrix",
    description: "Explore Orvitrix work across web products, mobile experiences, assistants, automation, APIs, and technical strategy.",
  },
  "/about": {
    title: "About Orvitrix | Product Engineering Team",
    description: "Meet the product engineering team connecting strategy, design, software, cloud systems, and intelligent automation.",
  },
  "/contact": {
    title: "Start a Project | Orvitrix",
    description: "Tell Orvitrix about your product idea, operational bottleneck, integration need, or automation opportunity.",
  },
  "/careers": {
    title: "Careers at Orvitrix",
    description: "Explore opportunities to build practical digital products and intelligent automation systems with Orvitrix.",
  },
  "/buildwebsite": { title: "Website Design & Development | Orvitrix", description: "Launch a responsive, conversion-focused website with clear content structure, strong performance, and an SEO-ready foundation." },
  "/mobileapps": { title: "Mobile App Development | Orvitrix", description: "Build focused Android and iOS product experiences with dependable APIs, account flows, testing, and release support." },
  "/webapps": { title: "Custom Web Application Development | Orvitrix", description: "Build secure, scalable SaaS products, dashboards, portals, and internal tools around real business workflows." },
  "/apigateways": { title: "API Gateway & Integration Services | Orvitrix", description: "Connect apps, vendors, and internal services through secure, documented, and maintainable APIs and gateway layers." },
  "/consultations": { title: "Product & Technology Consultation | Orvitrix", description: "Clarify product scope, architecture, technical risk, and the best path from idea to a useful first release." },
  "/adcreatives": { title: "Digital Ad Creative Services | Orvitrix", description: "Create focused campaign visuals and testable creative variations for digital advertising channels." },
  "/webscrapers": { title: "Web Scraping & Data Automation | Orvitrix", description: "Build reliable collection, cleanup, scheduling, and export workflows for structured business data." },
  "/crmplatforms": { title: "Custom CRM Platform Development | Orvitrix", description: "Connect leads, customers, deals, communication, automation, and reporting in a CRM designed for your workflow." },
  "/desktopapps": { title: "Desktop Application Development | Orvitrix", description: "Build native desktop tools for Windows, macOS, or Linux with offline-aware workflows and system integrations." },
  "/aiagents": { title: "AI Agent & Workflow Automation | Orvitrix", description: "Build practical AI agents with trusted context, approved tools, human review, guardrails, and measurable workflows." },
};

function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const fallback = {
      title: "Orvitrix | Digital Products & Automation",
      description: "Explore a focused Orvitrix service for digital product engineering and intelligent automation.",
    };
    const meta = routeMeta[pathname] || fallback;
    document.title = meta.title;

    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute("content", meta.description);

    const openGraphTitle = document.querySelector('meta[property="og:title"]');
    if (openGraphTitle) openGraphTitle.setAttribute("content", meta.title);

    const openGraphDescription = document.querySelector('meta[property="og:description"]');
    if (openGraphDescription) openGraphDescription.setAttribute("content", meta.description);

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://orvitrix.com" + (pathname === "/" ? "" : pathname));
  }, [pathname]);

  return null;
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <RouteMeta />
      <SiteAtmosphere />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/technologies" element={<Technologies />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/buildwebsite" element={<BuildWebsite />} />
        <Route path="/adcreatives" element={<AdCreatives />} />
        <Route path="/consultations" element={<Consultation />} />
        <Route path="/webapps" element={<WebApplications />} />
        <Route path="/mobileapps" element={<MobileApps />} />
        <Route path="/apigateways" element={<APIGateways />} />
        <Route path="/crmplatforms" element={<CRMPlatforms />} />
        <Route path="/aiagents" element={<AIAgents />} />
        <Route path="/desktopapps" element={<DesktopApps />} />
        <Route path="/webscrapers" element={<WebScrapers />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
